import React, { useEffect, useRef, useState } from 'react';
import useBuiltModalEscape from './useBuiltModalEscape';

const DEFAULT_CENTER = { lat: -41.0, lng: 173.0 };

const normalizePosition = (value) => {
  if (!value) return null;
  const lat = Number(value.lat ?? value.latitude);
  const lng = Number(value.lng ?? value.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

export const MapPinIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
    <path d="M12 2.5a7 7 0 0 0-7 7c0 5.25 7 12 7 12s7-6.75 7-12a7 7 0 0 0-7-7Zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z" />
  </svg>
);

export default function BuiltCoordinatePicker({
  isOpen,
  address = '',
  initialCoordinates = null,
  onCancel = () => {},
  onApply = () => {},
}) {
  const mapContainerRef = useRef(null);
  const [position, setPosition] = useState(normalizePosition(initialCoordinates));
  const [message, setMessage] = useState('');

  useBuiltModalEscape(isOpen, onCancel);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    let marker = null;

    const initialize = async () => {
      if (!window.google?.maps?.Map || !mapContainerRef.current) {
        setMessage('The map is still loading. Close this window and try again in a moment.');
        return;
      }

      let start = normalizePosition(initialCoordinates);
      if (!start && address && window.google.maps.Geocoder) {
        try {
          const response = await new window.google.maps.Geocoder().geocode({ address });
          const location = response.results?.[0]?.geometry?.location;
          if (location) start = { lat: location.lat(), lng: location.lng() };
        } catch (error) {
          // The user can still place the marker manually from the NZ overview.
        }
      }
      if (cancelled) return;

      const initial = start || DEFAULT_CENTER;
      setPosition(start);
      const map = new window.google.maps.Map(mapContainerRef.current, {
        center: initial,
        zoom: start ? 19 : 5,
        mapTypeId: 'satellite',
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: true,
      });

      marker = new window.google.maps.Marker({
        map,
        position: initial,
        draggable: true,
        title: 'Drag to the home location',
      });

      const updatePosition = (latLng) => {
        if (!latLng) return;
        setPosition({ lat: latLng.lat(), lng: latLng.lng() });
        setMessage('');
      };
      marker.addListener('dragend', (event) => updatePosition(event.latLng));
      map.addListener('click', (event) => {
        marker.setPosition(event.latLng);
        updatePosition(event.latLng);
      });
      if (!start) setMessage('Click the map to place the marker, then drag it for precision.');
    };

    setMessage('');
    initialize();
    return () => {
      cancelled = true;
      if (marker) marker.setMap(null);
    };
  }, [address, initialCoordinates, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="stock-admin-overlay built-coordinate-picker-overlay" onClick={(event) => { event.stopPropagation(); onCancel(); }}>
      <div className="built-coordinate-picker-panel" onClick={(event) => event.stopPropagation()}>
        <header className="built-review-header">
          <div>
            <div className="stock-admin-title">Set Home Location</div>
            <div className="built-review-progress">Click or drag the marker to the exact building location.</div>
          </div>
          <button type="button" className="stock-admin-close-button built-admin-close-button" onClick={onCancel} aria-label="Close map picker">×</button>
        </header>
        <div ref={mapContainerRef} className="built-coordinate-picker-map" aria-label="Home location map" />
        <footer className="stock-admin-footer">
          <div className="stock-admin-footer-status">{message || (position ? `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}` : '')}</div>
          <div className="stock-admin-footer-actions">
            <button type="button" className="stock-admin-secondary-button" onClick={onCancel}>Cancel</button>
            <button type="button" className="stock-admin-primary-button" disabled={!position} onClick={() => onApply(position)}>Use Location</button>
          </div>
        </footer>
      </div>
    </div>
  );
}
